import { Database } from "lucide-react";
import React, { useMemo } from "react";
import type { EvidenceEvent, RecordSourceRef } from "../../../types/evidence";
import {
	displaySystemName,
	recordAsOf,
	recordLocator,
} from "../../../utils/sourceLabels";
import "./EvidenceCards.css";

interface RecordFieldCardProps {
	event: EvidenceEvent;
}

interface DecoratedRecordRef {
	key: string;
	system: string;
	locator: string;
	asOf: string | null;
}

const decorateRecordRefs = (event: EvidenceEvent): DecoratedRecordRef[] =>
	event.source_refs
		.filter((r): r is RecordSourceRef => r.kind === "record")
		.map((ref, index) => ({
			key: `${ref.record_id}-${ref.field}-${index}`,
			system: displaySystemName(ref.source_system),
			locator: recordLocator(ref),
			asOf: recordAsOf(ref),
		}));

/**
 * One field of a CRM (or other record-shaped) row as evidence.
 *
 * Rows are static: a DealCloud value is a database field, not a file, and there
 * is no endpoint that opens one. Click-through needs a record card the app does
 * not have — DEFERRED to v2. What the row does carry is the full locator
 * (`CED-003 › facilities › commitment_usd`) and, when the snapshot's sync date
 * is known, an as-of stamp, so a reader can re-query it by hand and knows how
 * stale it may be.
 */
const RecordFieldCard: React.FC<RecordFieldCardProps> = ({ event }) => {
	const decorated = useMemo(() => decorateRecordRefs(event), [event]);

	return (
		<article className="evidence-card">
			<header className="evidence-card-header">
				<Database className="evidence-card-header-icon" aria-hidden />
				<div className="evidence-card-header-body">
					<h3 className="evidence-card-title">{event.title}</h3>
					<p className="evidence-card-summary">{event.summary}</p>
				</div>
			</header>
			<ul className="evidence-card-list">
				{decorated.map(({ key, system, locator, asOf }) => (
					<li key={key} className="evidence-card-row">
						<div className="evidence-card-row-static">
							<span className="evidence-card-filename">{system}</span>
							<span className="evidence-card-cell-ref">{locator}</span>
							{asOf && <span className="evidence-card-meta">· {asOf}</span>}
						</div>
					</li>
				))}
			</ul>
		</article>
	);
};

export default React.memo(RecordFieldCard);
